import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Person,
  Settings,
  Logout,
  DirectionsBike,
  CloudUpload,
  Air,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../auth/AuthModal';
import MuiLink from '@mui/material/Link';

const Header = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElNav, setAnchorElNav] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  
  // State to manage cookie banner visibility
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookies-consent');
    if (cookieConsent == null) {
      setShowCookieBanner(true);
    }
    // Check if the cookie consent was given more than one year ago
    const consentTimestamp = localStorage.getItem('cookies-consent-timestamp');
    if (consentTimestamp) {
      const oneYearAgo = Date.now() - 365 * 24 * 60 * 60 * 1000; // One year in milliseconds
      if (parseInt(consentTimestamp) < oneYearAgo) {
        localStorage.removeItem('cookies-consent');
        localStorage.removeItem('cookies-consent-timestamp');
        setShowCookieBanner(true);
      }
    }

  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookies-consent', 'true');
    localStorage.setItem('cookies-consent-timestamp', Date.now().toString());
    setShowCookieBanner(false);
  };

  const handleCancelCookies = () => {
    localStorage.setItem('cookies-consent', 'false');
    localStorage.setItem('cookies-consent-timestamp', Date.now().toString());
    setShowCookieBanner(false);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorElUser(null);
  };

  const handleNavMenuOpen = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleNavMenuClose = () => {
    setAnchorElNav(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  return (
    <>
    {showCookieBanner && (
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          zIndex: 1500,
          background: 'rgba(24, 24, 28, 0.98)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: { xs: '10px 16px', sm: '12px 24px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 2 },
          color: 'white',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.3)',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 300,
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
            textAlign: { xs: 'center', sm: 'left' },
            flex: 1,
          }}
        >
          {t('cookiesBanner.message').split('<cookiesLink>')[0]}
          <MuiLink
            component={RouterLink}
            to="/cookies-policy"
            sx={{ color: '#2196F3', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {t('cookiesBanner.message').split('<cookiesLink>')[1].split('</cookiesLink>')[0]}
          </MuiLink>
          {t('cookiesBanner.message').split('</cookiesLink>')[1]}
        </Typography>
        <Button
          onClick={handleCancelCookies}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 300,
            fontSize: { xs: '0.8rem', sm: '0.9rem' },
            background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.1) 30%, rgba(33, 203, 243, 0.1) 90%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            px: { xs: 2, sm: 3 },
            py: 0.5,
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2) 30%, rgba(33, 203, 243, 0.2) 90%)',
            },
            minWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          {t('cookiesBanner.cancel')}
        </Button>
        <Button
          onClick={handleAcceptCookies}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 300,
            fontSize: { xs: '0.8rem', sm: '0.9rem' },
            background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.1) 30%, rgba(33, 203, 243, 0.1) 90%)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            px: { xs: 2, sm: 3 },
            py: 0.5,
            color: 'white',
            '&:hover': {
              background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2) 30%, rgba(33, 203, 243, 0.2) 90%)',
            },
            minWidth: { xs: '100%', sm: 'auto' },
          }}
        >
          {t('cookiesBanner.accept')}
        </Button>
      </Box>
    )}
    <AppBar
      position="fixed"
      sx={{
        background: 'rgba(18, 18, 18, 0.8)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: { xs: 1, sm: 2 }, // Reduced padding on mobile
        }}
      >
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 300,
              letterSpacing: { xs: '0.1em', sm: '0.2em' },
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
              mr: { xs: 1, sm: 4 },
            }}
          >
            AERO
          </Typography>

          {/* Navigation Links (Visible on Desktop) */}
          {user && (
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Button
                component={RouterLink}
                to="/activity"
                color="inherit"
                sx={{
                  mr: 2,
                  textTransform: 'none',
                  fontWeight: 300,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                }}
                startIcon={<DirectionsBike />}
              >
                {t('navbar.myActivities')}
              </Button>
              <Button
                component={RouterLink}
                to="/upload"
                color="inherit"
                sx={{
                  textTransform: 'none',
                  fontWeight: 300,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                }}
                startIcon={<CloudUpload />}
              >
                {t('navbar.uploadActivity')}
              </Button>
              <Button
                component={RouterLink}
                to="/simulator"
                color="inherit"
                sx={{
                  textTransform: 'none',
                  fontWeight: 300,
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                }}
                startIcon={<Air />}
              >
                {t('navbar.simulator')}
              </Button>
            </Box>
          )}
        </Box>

        {/* Right Side: User Menu or Login Button */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Hamburger Menu for Mobile Navigation */}
          {user && (
            <IconButton
              color="inherit"
              onClick={handleNavMenuOpen}
              sx={{ display: { xs: 'flex', sm: 'none' }, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* User Menu or Login Button */}
          {user ? (
            <>
              <Button
                onClick={handleUserMenuOpen}
                sx={{
                  textTransform: 'none',
                  background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.1) 30%, rgba(33, 203, 243, 0.1) 90%)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: 2,
                  px: { xs: 1, sm: 2 },
                  py: 0.5,
                  '&:hover': {
                    background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2) 30%, rgba(33, 203, 243, 0.2) 90%)',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    bgcolor: 'primary.main',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    mr: 1,
                  }}
                >
                  {user.username ? user.username[0].toUpperCase() : 'U'}
                </Avatar>
                <Typography
                  sx={{
                    color: 'white',
                    fontWeight: 300,
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    display: { xs: 'none', sm: 'block' }, // Hide username on mobile
                  }}
                >
                  {user.username}
                </Typography>
              </Button>
              <Menu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={handleUserMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    background: 'rgba(24, 24, 28, 0.98)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    minWidth: { xs: 200, sm: 250 },
                    color: 'white',
                  },
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.1) 30%, rgba(33, 203, 243, 0.1) 90%)',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 300,
                      color: 'rgba(255, 255, 255, 0.7)',
                      mb: 0.5,
                    }}
                  >
                    {t('navbar.welcome')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 400,
                      color: '#2196F3',
                    }}
                  >
                    {user.username}
                  </Typography>
                </Box>
                <MenuItem
                  component={RouterLink}
                  to="/profile"
                  onClick={handleUserMenuClose}
                  sx={{
                    py: 1.5,
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(33, 150, 243, 0.1)',
                    },
                  }}
                >
                  <ListItemIcon>
                    <Person fontSize="small" sx={{ color: '#2196F3' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('navbar.profile')}
                    primaryTypographyProps={{
                      sx: { fontWeight: 300 },
                    }}
                  />
                </MenuItem>
                <MenuItem
                  component={RouterLink}
                  to="/settings"
                  onClick={handleUserMenuClose}
                  sx={{
                    py: 1.5,
                    color: 'white',
                    '&:hover': {
                      background: 'rgba(33, 150, 243, 0.1)',
                    },
                  }}
                >
                  <ListItemIcon>
                    <Settings fontSize="small" sx={{ color: '#2196F3' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('navbar.settings')}
                    primaryTypographyProps={{
                      sx: { fontWeight: 300 },
                    }}
                  />
                </MenuItem>
                <Box sx={{ p: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    onClick={handleLogout}
                    startIcon={<Logout />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 300,
                      py: 1,
                    }}
                  >
                    {t('common.logout')}
                  </Button>
                </Box>
              </Menu>
            </>
          ) : (
            <Button
              color="inherit"
              onClick={() => setAuthModalOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 300,
                background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.1) 30%, rgba(33, 203, 243, 0.1) 90%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
                px: { xs: 2, sm: 3 },
                py: 0.5,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                '&:hover': {
                  background: 'linear-gradient(45deg, rgba(33, 150, 243, 0.2) 30%, rgba(33, 203, 243, 0.2) 90%)',
                },
              }}
            >
              {t('auth.login.title')}
            </Button>
          )}
        </Box>

        {/* Mobile Navigation Menu */}
        {user && (
          <Menu
            anchorEl={anchorElNav}
            open={Boolean(anchorElNav)}
            onClose={handleNavMenuClose}
            PaperProps={{
              sx: {
                mt: 1,
                background: 'rgba(24, 24, 28, 0.98)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                minWidth: 200,
                color: 'white',
              },
            }}
          >
            <MenuItem
              component={RouterLink}
              to="/activity"
              onClick={handleNavMenuClose}
              sx={{
                py: 1.5,
                color: 'white',
                '&:hover': {
                  background: 'rgba(33, 150, 243, 0.1)',
                },
              }}
            >
              <ListItemIcon>
                <DirectionsBike fontSize="small" sx={{ color: '#2196F3' }} />
              </ListItemIcon>
              <ListItemText
                primary={t('navbar.myActivities')}
                primaryTypographyProps={{
                  sx: { fontWeight: 300 },
                }}
              />
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/upload"
              onClick={handleNavMenuClose}
              sx={{
                py: 1.5,
                color: 'white',
                '&:hover': {
                  background: 'rgba(33, 150, 243, 0.1)',
                },
              }}
            >
              <ListItemIcon>
                <CloudUpload fontSize="small" sx={{ color: '#2196F3' }} />
              </ListItemIcon>
              <ListItemText
                primary={t('navbar.uploadActivity')}
                primaryTypographyProps={{
                  sx: { fontWeight: 300 },
                }}
              />
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/simulator"
              onClick={handleNavMenuClose}
              sx={{
                py: 1.5,
                color: 'white',
                '&:hover': {
                  background: 'rgba(33, 150, 243, 0.1)',
                },
              }}
            >
              <ListItemIcon>
                <Air fontSize="small" sx={{ color: '#2196F3' }} />
              </ListItemIcon>
              <ListItemText
                primary={t('navbar.simulator')}
                primaryTypographyProps={{
                  sx: { fontWeight: 300 },
                }}
              />
            </MenuItem>
          </Menu>
        )}
      </Toolbar>
      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </AppBar>
    </>
  );
};

export default Header;